import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import { Card, Button, Divider, Select, Form, Dropdown, Modal, Menu, Row, Col, Input, Collapse, Skeleton } from 'antd';
import VariantItem from '../components/VariantItem';
import history from '../modules/history';
import {
  EllipsisOutlined,
  DeleteOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  BulbOutlined,
  EditOutlined,
  AlignLeftOutlined,
  RiseOutlined,
} from '@ant-design/icons';

const { TextArea } = Input;
const { Panel } = Collapse;
const { Option } = Select;
const FormItem = Form.Item;

export default class PageItem extends React.Component {
  constructor(props) {
    super(props);
    this.state.commonPath = ('path' in this.props) ? this.props.path : '';
  }

  state = {
    totalDistribution: 0,
    commonPath: '',

    // reflects the value typed by the user in the subdomain field.
    // this might be out of sync with the value in the store prop.
    // Hence marked as a state.
    subdomainBeingChecked: '',
  };

  static propTypes = {
    urlsafe: PropTypes.string,
    name: PropTypes.string,
    note: PropTypes.string,
    tutorial: PropTypes.bool,
    distribution: PropTypes.number,
    subdomain: PropTypes.string,
    path: PropTypes.string,
    url: PropTypes.string,
    variants: PropTypes.array,
    status: PropTypes.string,

    subdomainCheckStatus: PropTypes.string, // status of outgoing check request
    subdomainCheckResponse: PropTypes.object, // response object received from server

    domains: PropTypes.array,

    onPageVariantAdd: PropTypes.func.isRequired,
    onPageDelete: PropTypes.func.isRequired,
    onPageVariantDelete: PropTypes.func.isRequired,
    onPageVariantClone: PropTypes.func.isRequired,
    onPageVariantAttributeChange: PropTypes.func.isRequired,
    onPageVariantDistributionChange: PropTypes.func.isRequired,
    checkPageSubdomainAvailability: PropTypes.func.isRequired,
    onPageAttributeChange: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.updateTotalDistribution();
  }

  componentDidUpdate(prevProps) {
    this.updateTotalDistribution();
  }

  isNotInDeletingMode = x => x.status !== 'deleting';

  onNameBlur = (e) => {
    const newVal = e.target.value;
    if (newVal !== this.props.name) {
      this.props.onPageAttributeChange(
        this.props.urlsafe,
        [{ key: 'name', type: 'string', val: newVal }]
      );
    }
  };

  onNoteBlur = (e) => {
    const newVal = e.target.value;
    if (newVal !== this.props.note) {
      this.props.onPageAttributeChange(
        this.props.urlsafe,
        [{ key: 'note', type: 'string', val: newVal }]
      );
    }
  };

  onDomainSelect = (value) => {
    this.props.onPageAttributeChange(this.props.urlsafe,
      [{ key: 'url', type: 'string', val: value }]
    );
  }

  onSubdomainBlur = (e) => {
    const newVal = e.target.value;
    if (newVal !== this.props.subdomain) {
      this.props.onPageAttributeChange(this.props.urlsafe,
        [{ key: 'subdomain', type: 'string', val: newVal }]
      );
    }
  }

  onPathBlur = (e) => {
    const newVal = e.target.value;
    if (newVal !== this.props.path) {
      // eventGA({
      //   category: 'pages',
      //   action: 'set_page_url',
      //   label: this.props.urlsafe,
      // });
      this.props.onPageAttributeChange(this.props.urlsafe,
        [{ key: 'path', type: 'string', val: newVal }]
      );
    }
  }

  onPageVariantAdd = () => {
    this.props.onPageVariantAdd(this.props.urlsafe);
  }
  // onPageDelete = () => {
  //   this.props.onPageDelete(this.props.urlsafe);
  // }
  onPageVariantDelete = (variantUrlsafe) => {
    this.props.onPageVariantDelete(this.props.urlsafe, variantUrlsafe);
  }
  onPageVariantClone = (variantUrlsafe) => {
    this.props.onPageVariantClone(this.props.urlsafe, variantUrlsafe);
  }
  onPageVariantAttributeChange = (variantUrlsafe, changes) => {
    this.props.onPageVariantAttributeChange(this.props.urlsafe, variantUrlsafe, changes);
    this.updateTotalDistribution();
  }

  onPageVariantDistibutionChange = (variantUrlsafe, distribution) => {
    this.props.onPageVariantDistributionChange(this.props.urlsafe, variantUrlsafe, distribution);
    this.updateTotalDistribution(variantUrlsafe, distribution);
  };

  onPathChange = (e) => {
    this.setState({
      commonPath: e.target.value,
    });
  };

  throttleCheckSubdomainAvailability = throttle((value) => {
    this.setState({
      subdomainBeingChecked: value,
    });
    this.props.checkPageSubdomainAvailability(this.props.urlsafe, value);
  }, 900);

  onSubdomainChange = (e) => {
    if (e.target.value.length > 0) {
      this.throttleCheckSubdomainAvailability(e.target.value);
    }
  };

  updateTotalDistribution = (
    variantUrlsafe = null,
    distribution = null
  ) => {
    const total = this.props.variants.reduce((prev, variant) => {
      let contribution = 0;
      if (variantUrlsafe !== null && variant.urlsafe === variantUrlsafe) {
        contribution = distribution;
      }
      else if (variant.enabled) {
        contribution = variant.distribution;
      }
      else {
        contribution = 0;
      }
      return (prev + contribution);
    }, 0);

    if (this.state.totalDistribution !== total) {
      this.setState({
        totalDistribution: total,
      });
    }
  };

  renderSubdomainValidateStatus = () => {
    if (!('subdomainCheckStatus' in this.props)) {
      return '';
    }
    const requestStatus = this.props.subdomainCheckStatus;

    if (typeof requestStatus === 'undefined') {
      return '';
    }

    if (requestStatus === 'checking') {
      return 'validating';
    }

    if (requestStatus === 'checked') {
      const serverResponse = this.props.subdomainCheckResponse;

      if (serverResponse.subdomain === this.state.subdomainBeingChecked) {
        return (serverResponse.available) ? 'success' : 'error';
      }
      return null;
    }
    // console.assert(false, 'some kind of error happened during subdomain check');
    return null;
  };

  renderVariantHeader = (variants) => {
    if (variants.length === 0) {
      return null;
    }
    const numVariants = variants.length;
    const variantStr = (numVariants > 1) ? 'variants' : 'variant';

    return (
      <React.Fragment>
        <span className="text-secondary">{numVariants} {variantStr}</span>
        <Divider type="vertical" />
        <Button type="link" onClick={this.onPageVariantAdd}>Add variant</Button>
      </React.Fragment>
    );
  }

  deleteAfterConfirm = () => {
    Modal.confirm({
      icon: <WarningOutlined />,
      title: "Are you sure?",
      content: "Your page, along with its variants and associated leads will be deleted. This action cannot be undone. If unsure, consider taking it offline instead.",
      cancelText: "Never Mind",
      cancelType: "primary",
      okText: "Delete",
      okType: "danger",
      maskClosable: true,
      onOk: () => {
        this.props.onPageDelete(this.props.urlsafe);
      },
    });
  };

  onLeadsClick = () => {
    history.push({
      pathname: '/leads',
      navPageUrlsafe: this.props.urlsafe,
      navVariantUrlsafe: 'all',
    });
  };

  render() {
    const {
      // urlsafe, // string based datastore id
      status, // idle -> loading -> idle
      name, note, // meta
      // distribution, // total
      subdomain, url, path, // domains
      variants,
      domains,
      tutorial,
      // subdomainCheckResponse,
    } = this.props;

    const isLoading = (typeof status === 'undefined') ? false : (status === 'loading');
    const emptyState = (variants && variants.length === 0);

    const filteredVariants = variants.filter(this.isNotInDeletingMode);
    const variantItems = filteredVariants.map((variant) =>
      (<VariantItem
        key={variant.urlsafe}

        pageUrlsafe={this.props.urlsafe}

        {...variant}
        tutorial={tutorial}

        totalDistribution={this.state.totalDistribution}

        onPageVariantDelete={this.onPageVariantDelete}
        onPageVariantClone={this.onPageVariantClone}
        onPageVariantAttributeChange={this.onPageVariantAttributeChange}

        onPageVariantDistibutionChange={this.onPageVariantDistibutionChange}
      />)
    );

    const hasOwnDomain = (url && url.length > 0);
    const hasPageHitsDomain = (subdomain && subdomain.length > 0);
    const appendPath = path ? (`/${path}`) : '';
    const ownDomain = hasOwnDomain ? (`https://${url}${appendPath}`) : '';
    const pageHitsDomain = hasPageHitsDomain ? (`https://${subdomain}.on-pagehits.com${appendPath}`) : '';
    let domainPanelHeaderNode = null;

    if (hasOwnDomain) {
      domainPanelHeaderNode = (
        <div className="d-flex align-items-center uifont">
          { ownDomain } <CheckCircleOutlined className="ml-2" />
        </div>
      );
    }
    else if (hasPageHitsDomain) {
      domainPanelHeaderNode = (
        <div className="d-flex align-items-center uifont">
          { pageHitsDomain } <CheckCircleOutlined className="ml-2" />
        </div>
      );
    }
    else {
      domainPanelHeaderNode = (
        <div className="d-flex align-items-center text-muted uifont">
          Set Page URL <BulbOutlined className="ml-2 text-white" />
        </div>
      );
    }

    const domainOptions = domains.map((domain) => (
      <Option key={domain.urlsafe} value={domain.url}>{ domain.url }</Option>
    ));

    const overflowMenu = (
      <Menu>
        <Menu.Item key="leads" onClick={this.onLeadsClick}>
          <RiseOutlined />View generated leads
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="delete" className="text-danger" onClick={this.deleteAfterConfirm}>
          <DeleteOutlined />Unpublish & Delete
        </Menu.Item>
      </Menu>
    );

    if (isLoading) {
      return (
        <Card className="mb-4">
          <Skeleton active />
          <div className="page__item__variant__inset">
            <Row gutter={16}>
              <Col
                xs={24} sm={12} md={12}
                lg={8} xl={6}
                style={{ padding: '8px' }}
              >
                <Card type="inner" className="mb-3">
                  <Skeleton active />
                </Card>
              </Col>
            </Row>
          </div>
        </Card>
      );
    }

    const urlCollapse = (
      <Collapse bordered={false} defaultActiveKey={[]} className="page__item__collapse">
        <Panel header={domainPanelHeaderNode} key="1">
          <div className="container-fluid px-0">
            <div className="row">
              <div className="col-12 col-lg-9 col-xl-8 col-xxl-6">

                <div className="text-white-50 mb-3">
                  Bring your own domain.
                  Or use PageHits's domain for free.
                  Or both, all at once.
                </div>

                <div className="mb-2 text-white">
                  Your domain &middot;&nbsp;
                  <Link to="/domains" className="text-white" style={{ textDecoration: 'underline' }}>Manage my domains</Link>
                </div>

                <div className="d-flex flex-row mb-4 bg-light align-items-center rounded-sm overflow-hidden">
                  <div className="mx-1" style={{ flexGrow: 0 }}>https://</div>
                  <div style={{ flex: "2 0" }}>
                    <Select
                      className="w-100 url__input"
                      placeholder="-- choose --"
                      onSelect={this.onDomainSelect}
                      defaultValue={url}
                    >
                      { domainOptions }
                    </Select>
                  </div>
                  <div className="mx-1" style={{ flexGrow: 0 }}>/</div>
                  <div style={{ flex: "1 0" }}>
                    <Input
                      className="url__input"
                      defaultValue="" placeholder="optional path"
                      onChange={this.onPathChange}
                      onBlur={this.onPathBlur}
                      value={this.state.commonPath}
                    />
                  </div>
                </div>


                <div className="mt-4 mb-2 text-white">PageHits domain</div>

                <div className="d-flex flex-row mb-4 bg-light align-items-center rounded-sm overflow-hidden">
                  <div className="mx-1" style={{ flexGrow: 0 }}>https://</div>
                  <div style={{ flex: "2 0" }}>
                    <FormItem
                      className="mb-0"
                      hasFeedback
                      validateStatus={this.renderSubdomainValidateStatus()}
                    >
                      <Input
                        className="url__input"
                        onChange={this.onSubdomainChange}
                        onBlur={this.onSubdomainBlur}
                        placeholder="subdomain"
                        defaultValue={subdomain}
                      />
                    </FormItem>
                  </div>
                  <div className="mx-1" style={{ flexGrow: 0 }}>.on-pagehits.com/</div>
                  <div style={{ flex: "1 0" }}>
                    <Input
                      className="url__input"
                      placeholder="optional path"
                      onChange={this.onPathChange}
                      onBlur={this.onPathBlur}
                      value={this.state.commonPath}
                    />
                  </div>
                </div>


              </div>
            </div>
          </div>
        </Panel>
      </Collapse>
    );

    const nameNote = (
      <React.Fragment>
        <div className="d-flex">
          <div className="d-inline-block d-flex" style={{ paddingTop: 10 }}>
            <EditOutlined className="text-muted" />
          </div>
          <Input
            placeholder="Name this page"
            defaultValue={name}
            onBlur={this.onNameBlur}
            onPressEnter={this.onNameBlur}
            className="page__name d-inline-block"
            spellCheck={false}
          />
        </div>
        <div className="d-flex">
          <div className="d-inline-block d-flex" style={{ paddingTop: 4 }}>
            <AlignLeftOutlined className="text-muted" />
          </div>
          <TextArea
            placeholder="Add a note..."
            defaultValue={note}
            onBlur={this.onNoteBlur}
            autoSize={{ maxRows: 5 }}
            className="page__note d-inline-block"
            spellCheck={false}
          />
        </div>
      </React.Fragment>
    );

    const overflowBtn = (
      <Dropdown
        overlay={overflowMenu}
        placement="bottomLeft"
        trigger={['click']}
      >
        <Button type="secondary" size="large" shape="circle" icon={<EllipsisOutlined />} className="shadow-sm" />
      </Dropdown>
    );

    const variantSection = (
      <div className="page__item__variant__inset">
        { emptyState ? (
          <div className="py-4 mb-4" style={{ textAlign: 'center', backgroundColor: '#F5F6FA' }}>
            <div className="text-secondary mb-4">This page does not have any variant yet</div>
            <Button type="primary" onClick={this.onPageVariantAdd} icon="plus">Add New Variant</Button>
          </div>
        ) : (
          <React.Fragment>
            { this.renderVariantHeader(variants) }
            <Row gutter={16}>
              { variantItems }
            </Row>
          </React.Fragment>
        )}
      </div>
    );

    return (
      <div className="container-fluid bg-white shadow rounded mb-4 overflow-hidden page__item">
        <div className="row">
          <div className="col-12 px-0">
            {urlCollapse}
          </div>
        </div>
        <div className="row position-relative shift-up">
          <div className="row-shader d-none d-lg-block bg-dark" />
          <div className="col-12 col-lg-9 col-xl-8 col-xxl-8 order-2 order-lg-1">
            <div className="bg-white rounded px-lg-3 px-0 py-0 py-lg-3 nameNoteWrap">
              {nameNote}
            </div>
          </div>
          <div className="col-12 col-lg-3 col-xl-2 col-xxl-3 order-1 order-lg-2 text-right text-lg-left">
            {overflowBtn}
          </div>
        </div>
        <div className="row">
          <div className="col-12 mt-3">
            {variantSection}
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/components/PageItem.jsx